import React, { useEffect, useRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Divider, Popover } from '@mui/material';

import './FilterPill.scss';

import { shortString } from '@/utils/global';

import RestartIcon from '@/components/Icons/icons/RestartIcon';
import TrashOutlined from '@/components/Icons/icons/TrashOutlined';

interface FilterPillProps {
  leftIcon?: React.ReactNode;
  filterLabel: string;
  selected?: string[];
  OptionsComponent: React.ComponentType<any>;
  onRemoveFilter: () => void;
  onResetSelection?: () => void;
  popoverWidth?: number;
  isOpen?: boolean;
  mode?: 'filter' | 'sort';
  onClose?: () => void;
}

const FilterPill: React.FC<FilterPillProps> = ({
  leftIcon,
  filterLabel,
  selected = [],
  OptionsComponent,
  onRemoveFilter,
  onResetSelection,
  popoverWidth = 280,
  isOpen = false,
  onClose,
  mode = 'filter',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const open = Boolean(anchorEl) || isOpen;
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && !anchorEl && triggerRef.current) {
      setAnchorEl(triggerRef.current);
    }
  }, [isOpen, anchorEl]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onClose?.();
  };

  const handleRemoveFilter = () => {
    onRemoveFilter();
    handleClose();
  };

  return (
    <div className="filter-pill__container">
      <div
        ref={triggerRef}
        onClick={handleClick}
        className={`filter-pill__trigger ${open ? 'filter-pill__trigger__active' : ''}`}>
        {leftIcon && <div className="filter-pill__left-icon">{leftIcon}</div>}
        <span className="filter-pill__label">{filterLabel}</span>
        {mode === 'filter' ? ':' : ''}
        {selected.length > 0 && (
          <div className="filter-pill__selected-container">
            <span className="filter-pill__selected">{shortString(selected[0], 15)}</span>
            <span className="filter-pill__selected-count">
              {selected.length > 1 && ` +${selected.length - 1} more`}
            </span>
          </div>
        )}
        <KeyboardArrowDownIcon
          className={`filter-pill__chevron ${open ? 'filter-pill__chevron--open' : ''}`}
          sx={{
            width: 24,
            height: 24,
            color: 'var(--text-default)',
          }}
        />
      </div>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            width: popoverWidth,
            marginTop: 4,
            borderRadius: 8,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          },
        }}>
        <div className="filter-pill__popover-content">
          <OptionsComponent />
        </div>
        <Divider sx={{ borderColor: 'var(--border-default)' }} />
        <div className="filter-pill__action-buttons">
          {onResetSelection && (
            <div
              onClick={onResetSelection}
              className={`filter-pill__action-button ${selected.length === 0 ? 'filter-pill__action-button--disabled' : ''}`}>
              <RestartIcon />
              <span className="filter-pill__action-button-text">Reset Selection</span>
            </div>
          )}
          <div onClick={handleRemoveFilter} className="filter-pill__action-button danger">
            <TrashOutlined color="currentColor" />
            <span>Remove {mode === 'filter' ? 'Filter' : 'Sorting'}</span>
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default FilterPill;
